import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import Box from "@memorang/ui/src/components/Box";
import { TextSkeleton } from "@memorang/ui/src/components/ChatUI/TextSkeleton";
import DialogWrapper from "@memorang/ui/src/components/DialogWrapper";
/* -----------------Icons--------------- */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Text } from "react-native-paper";
import type { FeedbackParams } from "../../feedback/types";
import { SummaryActions } from "../../qa-search/SummaryActions";
import type { HandleTracking } from "../../types";
import { events } from "../constants";
import type { SessionMetadata } from "../types";
import FullReportContent from "./FullReportContent";

type Props = {
	sessionAnalysisData: string;
	visible: boolean;
	handleDismiss: () => void;
	feedbackDialogParams?: FeedbackParams;
	handleTracking: HandleTracking;
	bottomContent?: React.ReactNode;
	sessionMetadata?: SessionMetadata;
	streaming: boolean;
	loading: boolean;
	fontSize?: number;
	appFont?: string;
};

const FullReportDialog = ({
	sessionAnalysisData,
	visible,
	handleDismiss,
	feedbackDialogParams,
	handleTracking,
	bottomContent,
	sessionMetadata,
	streaming,
	loading,
	fontSize,
	appFont,
}: Props) => {
	const [openTime, setOpenTime] = useState(0);
	const { t } = useTranslation();

	useEffect(() => {
		if (visible && !streaming) {
			setOpenTime(Date.now());
			handleTracking(events.fullReportViewed);
		}
	}, [handleTracking, visible, streaming]);

	const handleOnDismiss = () => {
		const readingTimeInSeconds = Math.floor((Date.now() - openTime) / 1000);
		handleTracking(events.readingTime, {
			readTime: `${readingTimeInSeconds} seconds`,
		});
		handleDismiss();
	};

	const SessionMetadata = () => {
		if (!sessionMetadata) {
			return null;
		}

		const { sessionDate, sessionTitle, numItems } = sessionMetadata;
		const caption = `${numItems} ${t("common.items")}, ${sessionDate}`;
		return (
			<Box alignItems="center" paddingBottom={16}>
				<Text>{sessionTitle}</Text>
				<Text
					variant="bodySmall"
					style={{
						fontStyle: "italic",
					}}
				>
					{caption}
				</Text>
			</Box>
		);
	};

	return (
		<DialogWrapper
			visible={visible}
			handleClose={handleOnDismiss}
			minHeight="60%"
		>
			<Dialog.Icon
				icon={(props) => (
					<Icon
						{...props}
						name="brain"
						size={20}
						accessibilityLabel="Analysis and Insights"
					/>
				)}
			/>
			<Dialog.Title
				style={{
					textAlign: "center",
					marginBottom: 0,
				}}
			>
				{t("report.sessionAnalysis.title")}
			</Dialog.Title>
			<SessionMetadata />
			<Dialog.ScrollArea
				style={{
					paddingHorizontal: 24,
					paddingTop: 8,
				}}
			>
				{loading ? (
					<Box paddingHorizontal={16}>
						<TextSkeleton numLines={8} height={18} />
					</Box>
				) : (
					<FullReportContent
						fontSize={fontSize}
						bottomContent={bottomContent}
						appFont={appFont}
						sessionAnalysisData={sessionAnalysisData}
					/>
				)}
			</Dialog.ScrollArea>
			<Dialog.Actions
				style={{
					alignItems: "flex-end",
				}}
			>
				<SummaryActions
					align="flex-end"
					showEmail
					text={sessionAnalysisData}
					handleTracking={handleTracking}
					streaming={streaming}
					feedbackDialogParams={feedbackDialogParams}
				/>
			</Dialog.Actions>
		</DialogWrapper>
	);
};

export default FullReportDialog;
